import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/Layout';
import YouTubeHeroSection from '../components/shared/YouTubeHeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import PaymentOptions from '../components/shared/PaymentOptions';
import { ParallaxSection, ParallaxElement, FadeInOnScroll } from '../components/shared/Parallax';
import { heroContent, tendeDaSoleTypes, installatoriInterni } from '../data/mock';
import { Shield, Sun, Palette, Settings, Factory, Users, Wrench, ChevronDown, X, Check, Ruler, Cog, MapPin } from 'lucide-react';

const TendeDaSolePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedTenda, setExpandedTenda] = useState(null);

  // Descrizioni approfondite per ogni tipo di tenda
  const tendaDetails = {
    "Tende a Bracci": {
      intro: "La soluzione più classica e versatile per terrazzi e balconi. I bracci estensibili permettono di ottenere ampie coperture mantenendo lo spazio sottostante completamente libero.",
      specs: [
        "Larghezza fino a 10 metri con sporgenze fino a 4 metri",
        "Braccia pieghevoli con catena in acciaio maggiorata per massima resistenza",
        "Struttura in alluminio verniciato disponibile in bianco o argento",
        "Centinaia di colori e fantasie per il tessuto Tempotest Parà",
        "Possibilità di illuminazione LED integrata",
        "Movimento manuale con arganello o motorizzato con telecomando",
        "Sensore vento e sole disponibile per chiusura automatica",
        "Garanzia struttura 6 anni, tessuto 5 anni"
      ],
      ideal: "Ideale per: terrazzi, balconi, dehors di bar e ristoranti, ingressi commerciali."
    },
    "Tende a Caduta": {
      intro: "Perfette per schermare finestre e verande dalla luce diretta. Scendono verticalmente con guide laterali che garantiscono stabilità anche in presenza di vento.",
      specs: [
        "Larghezza fino a 4.80 metri con caduta fino a 3.20 metri",
        "Guide laterali in alluminio ad alta resistenza",
        "Possibilità di aggiungere braccetti per maggiore sporgenza",
        "Tessuti filtranti o oscuranti a seconda delle esigenze",
        "Struttura minimal che si integra con ogni architettura",
        "Movimento manuale o motorizzato",
        "Sistema antivento con anemometro disponibile",
        "Garanzia struttura 6 anni, tessuto 5 anni"
      ],
      ideal: "Ideale per: finestre, verande, chiusure laterali di pergole, protezione privacy."
    },
    "Cappottine": {
      intro: "L'eleganza senza tempo delle cappottine dona carattere a ingressi, vetrine e finestre. Disponibili in forma quadrata o tonda, con struttura fissa o retraibile.",
      specs: [
        "Larghezza fino a 5 metri con sporgenza fino a 1.60 metri",
        "Struttura in alluminio con curve sagomate",
        "Forme disponibili: quadrata classica o tonda tradizionale",
        "Tessuti acrilici Tempotest Parà in centinaia di colori",
        "Possibilità di personalizzazione con scritte e loghi",
        "Versione fissa o con telo retraibile",
        "Illuminazione LED opzionale per effetto scenico serale",
        "Garanzia struttura 6 anni, tessuto 5 anni"
      ],
      ideal: "Ideale per: negozi, bar, ristoranti, ingressi di abitazioni, vetrine."
    },
    "Tende Attico": {
      intro: "Progettate per coperture orizzontali di terrazzi e attici. Il telo scorre su guide laterali coprendo anche grandi superfici e può essere fermato in qualsiasi posizione.",
      specs: [
        "Larghezza fino a 4.80 metri con sporgenza fino a 5 metri",
        "Guide perimetrali in alluminio rinforzato",
        "Telo fermabile a qualsiasi altezza durante la corsa",
        "Design minimalista per integrazione architettonica",
        "Struttura leggera ma resistente anche in zone ventose",
        "Movimento motorizzato con telecomando incluso",
        "Sistema antivento con anemometro integrato",
        "Garanzia struttura 6 anni, tessuto 5 anni"
      ],
      ideal: "Ideale per: attici, terrazzi, coperture orizzontali, patii."
    },
    "Tende Ermetiche": {
      intro: "La tecnologia più avanzata per la schermatura solare. Il sistema di chiusura a zip integrato nelle guide laterali blocca il tessuto in posizione, garantendo tenuta ermetica contro vento e infiltrazioni di luce.",
      specs: [
        "Sistema brevettato con chiusura a zip nelle guide laterali",
        "Tenuta antivento certificata fino a classe 6",
        "Blocco luminoso completo sui bordi laterali",
        "Profili sottili per un design elegante e discreto",
        "Cassetta di protezione per il tessuto quando chiusa",
        "Movimento esclusivamente motorizzato",
        "Tessuti tecnici ad alte prestazioni",
        "Garanzia struttura 6 anni, tessuto 5 anni"
      ],
      ideal: "Ideale per: facciate vetrate, uffici, ambienti che richiedono oscuramento totale."
    }
  };

  const toggleExpand = (tendaName) => {
    setExpandedTenda(expandedTenda === tendaName ? null : tendaName);
  };

  const benefits = [
    {
      icon: Factory,
      title: "Produzione Propria",
      description: "Siamo produttori diretti con sartoria e magazzino interno. Nessun intermediario."
    },
    {
      icon: Sun,
      title: "Protezione Solare",
      description: "Tessuti Tempotest Parà certificati con protezione UV fino al 97%."
    },
    {
      icon: Palette,
      title: "1000+ Tessuti",
      description: "Ampia gamma di tessuti, colori e finiture. Vieni in showroom a scegliere."
    },
    {
      icon: Users,
      title: "Montatori Interni",
      description: "Solo i nostri dipendenti installano: qualità e precisione garantite."
    }
  ];

  return (
    <Layout>
      {/* Hero Section with YouTube Video */}
      <YouTubeHeroSection
        title={heroContent.tendeDaSole.title}
        subtitle={heroContent.tendeDaSole.subtitle}
        ctaText="Richiedi un progetto su misura"
        ctaLink="/preventivo"
        youtubeId="-odCJbhEnes"
        fallbackImageUrl={heroContent.tendeDaSole.imageUrl}
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Tende da Sole
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                Protezione e stile per ogni ambiente
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed">
                Le nostre tende da sole non sono semplici coperture. Sono elementi di design che 
                valorizzano l'architettura, proteggono dai raggi UV e creano ambienti confortevoli 
                per vivere l'outdoor in ogni momento della giornata.
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 lg:py-32 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="I Vantaggi"
              title="Perché scegliere le nostre tende"
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="text-center p-8 group">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-sky-500/10 flex items-center justify-center group-hover:bg-sky-500 transition-colors duration-300">
                    <benefit.icon className="w-7 h-7 text-sky-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Types Section with Images - CLICKABLE */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Tipologie"
              title="Soluzioni per ogni esigenza"
              description="Clicca su ogni tipologia per scoprire le specifiche tecniche. Ogni tenda è prodotta nella nostra sartoria a Pisa."
            />
          </FadeInOnScroll>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tendeDaSoleTypes.map((type, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div 
                  className={`group overflow-hidden border transition-all duration-500 cursor-pointer ${
                    expandedTenda === type.name 
                      ? 'border-sky-500 shadow-xl ring-2 ring-sky-500/20' 
                      : 'border-gray-100 hover:border-sky-200 hover:shadow-xl'
                  }`}
                  onClick={() => toggleExpand(type.name)}
                >
                  {/* Image */}
                  {type.image && (
                    <div className="aspect-[4/3] overflow-hidden relative">
                      <img 
                        src={type.image} 
                        alt={type.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Click indicator */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <span className="text-white text-sm font-medium flex items-center gap-2">
                          <Cog className="w-4 h-4" />
                          Clicca per specifiche tecniche
                        </span>
                      </div>
                    </div>
                  )}
                  {/* Content */}
                  <div className="p-6 lg:p-8">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-xl font-medium transition-colors duration-300 ${
                        expandedTenda === type.name ? 'text-sky-500' : 'text-gray-900 group-hover:text-sky-500'
                      }`}>
                        {type.name}
                      </h3>
                      <ChevronDown className={`w-5 h-5 text-sky-500 transition-transform duration-300 ${
                        expandedTenda === type.name ? 'rotate-180' : ''
                      }`} />
                    </div>
                    <p className="text-gray-600 font-light leading-relaxed mb-4 text-sm">
                      {type.description}
                    </p>
                    <ul className="space-y-2">
                      {type.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center gap-2 text-sm text-gray-600 font-light">
                          <span className="w-1.5 h-1.5 rounded-full bg-sky-500 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedTenda === type.name && tendaDetails[type.name] && (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-6 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-lg font-medium text-gray-900">
                        Approfondimento
                      </h4>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setExpandedTenda(null); }}
                        className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5 text-gray-500" />
                      </button>
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {tendaDetails[type.name].intro}
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                      {tendaDetails[type.name].specs.map((spec, sIndex) => (
                        <div key={sIndex} className="flex items-start gap-3">
                          <Check className="w-4 h-4 text-sky-500 flex-shrink-0 mt-1" />
                          <span className="text-sm text-gray-600">{spec}</span>
                        </div>
                      ))}
                    </div>
                    
                    <p className="text-sm text-sky-600 font-medium mb-6">
                      {tendaDetails[type.name].ideal}
                    </p>
                    
                    <a 
                      href="/preventivo" 
                      className="inline-flex items-center gap-2 bg-sky-500 text-white px-6 py-3 rounded-lg hover:bg-sky-600 transition-colors text-sm font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Richiedi preventivo
                    </a>
                  </div>
                )}
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Production Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-full text-sm font-medium mb-6">
                <Factory className="w-4 h-4" />
                PRODUTTORI DIRETTI
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 mb-6">
                La nostra <span className="text-sky-500 font-medium">sartoria</span> e il nostro <span className="text-sky-500 font-medium">magazzino</span>
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed mb-8">
                A differenza dei rivenditori, noi <strong>produciamo direttamente</strong> ogni tenda nel nostro laboratorio a Pisa. 
                Dalla scelta del tessuto alla cucitura finale, tutto avviene sotto il nostro controllo. 
                Questo significa <strong>qualità superiore</strong> e <strong>prezzi più competitivi</strong>.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Wrench className="w-8 h-8 text-sky-500 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">Sartoria Interna</h4>
                  <p className="text-sm text-gray-600">Taglio e cucitura professionale nel nostro laboratorio</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Factory className="w-8 h-8 text-sky-500 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">Magazzino Proprio</h4>
                  <p className="text-sm text-gray-600">Stock tessuti e componenti sempre disponibili</p>
                </div>
                <div className="bg-white p-6 rounded-lg shadow-sm">
                  <Users className="w-8 h-8 text-sky-500 mx-auto mb-3" />
                  <h4 className="font-medium text-gray-900 mb-2">Team Dedicato</h4>
                  <p className="text-sm text-gray-600">Artigiani esperti con anni di esperienza</p>
                </div>
              </div>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Montatori Interni Banner */}
      <section className="py-12 bg-gray-900 text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
            <Users className="w-12 h-12 text-sky-400" />
            <div>
              <h3 className="text-xl font-medium mb-1">{installatoriInterni.title}</h3>
              <p className="text-gray-300 font-light">{installatoriInterni.text}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Parallax Visual Section */}
      {/* Solo Tempotest Parà Section with YouTube Video */}
      <section className="relative h-[60vh] flex items-center overflow-hidden bg-gray-900">
        {/* YouTube Video Background - scalato per nascondere branding */}
        <div className="absolute inset-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="absolute -inset-[18%]">
            <iframe
              src="https://www.youtube-nocookie.com/embed/WLIX2qEX7Ss?autoplay=1&mute=1&loop=1&playlist=WLIX2qEX7Ss&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&fs=0&iv_load_policy=3&cc_load_policy=0"
              title="Tempotest Parà - Giovanni Tende"
              className="w-full h-full"
              style={{ border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            />
          </div>
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/40" />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 lg:px-12">
          <FadeInOnScroll className="max-w-xl">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              Solo tessuti Tempotest Parà
            </h2>
            <p className="text-lg text-white/80 font-light">
              Utilizziamo esclusivamente tessuti <strong className="text-white">Tempotest Parà</strong>, 
              il marchio italiano leader nella produzione di tessuti acrilici per outdoor. 
              Garanzia di 5 anni, colori inalterabili nel tempo e protezione UV certificata fino al 97%.
            </p>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Magazzino Tessuti Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInOnScroll>
              <div>
                <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-amber-600">
                  Tocca con Mano
                </span>
                <h2 className="text-3xl md:text-4xl font-light text-gray-900 leading-tight mb-6">
                  Il nostro magazzino tessuti Tempotest Parà
                </h2>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-6">
                  Disponiamo di un <strong>ampio magazzino interno</strong> con centinaia di tessuti 
                  Tempotest Parà pronti per la visione. Vieni a trovarci nel nostro showroom per 
                  <strong> scegliere e toccare con mano</strong> i tessuti che preferirai per la tua tenda.
                </p>
                <p className="text-lg text-gray-600 font-light leading-relaxed mb-8">
                  Il nostro personale esperto ti guiderà nella scelta del tessuto più adatto 
                  alle tue esigenze, valutando insieme colori, fantasie, grammatura e caratteristiche tecniche.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a 
                    href="/preventivo" 
                    className="inline-flex items-center justify-center gap-2 bg-amber-500 text-white px-6 py-3 rounded-lg hover:bg-amber-600 transition-colors font-medium"
                  >
                    <MapPin className="w-5 h-5" />
                    Vieni in Showroom
                  </a>
                  <a 
                    href="/tessuti" 
                    className="inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:border-amber-500 hover:text-amber-600 transition-colors font-medium"
                  >
                    <Palette className="w-5 h-5" />
                    Sfoglia il Catalogo
                  </a>
                </div>
              </div>
            </FadeInOnScroll>
            <FadeInOnScroll delay={200}>
              <div className="relative">
                <div className="bg-white p-8 rounded-2xl shadow-xl">
                  <div className="grid grid-cols-2 gap-6 mb-6">
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <div className="text-4xl font-bold text-amber-500 mb-2">1000+</div>
                      <div className="text-sm text-gray-600">Colori disponibili</div>
                    </div>
                    <div className="text-center p-4 bg-amber-50 rounded-lg">
                      <div className="text-4xl font-bold text-amber-500 mb-2">100%</div>
                      <div className="text-sm text-gray-600">Made in Italy</div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">Tessuti visionabili in showroom</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">Campioni gratuiti da portare a casa</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">Consulenza personalizzata inclusa</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Check className="w-5 h-5 text-green-500" />
                      <span className="text-gray-700">Pronta disponibilità per colori più richiesti</span>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInOnScroll>
          </div>
        </div>
      </section>

      {/* Tempotest Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto">
              <div className="flex flex-col lg:flex-row items-center gap-12">
                <div className="lg:w-1/3 text-center">
                  <div className="bg-white p-6 rounded-lg shadow-lg border border-gray-100">
                    <img 
                      src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQvqajmmtjNoDv1FwOm8O2Ib87mF9FxxHMy9Q&s" 
                      alt="Tempotest by Parà - Italian Performance Fabrics"
                      className="w-full max-w-[200px] mx-auto"
                    />
                    <p className="text-sm text-gray-500 mt-4">Partner Ufficiale</p>
                  </div>
                </div>
                <div className="lg:w-2/3">
                  <h3 className="text-2xl md:text-3xl font-light text-gray-900 mb-4">
                    Perché scegliamo solo Tempotest Parà
                  </h3>
                  <p className="text-gray-600 font-light leading-relaxed mb-6">
                    Da oltre 60 anni collaboriamo esclusivamente con Tempotest Parà, azienda italiana 
                    di riferimento mondiale nella produzione di tessuti per l'outdoor. I loro tessuti acrilici 
                    tinti in massa garantiscono:
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-sky-500 mt-2 flex-shrink-0" />
                      <span className="text-gray-600"><strong>Garanzia 5 anni</strong> su colore e resistenza del tessuto</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-sky-500 mt-2 flex-shrink-0" />
                      <span className="text-gray-600"><strong>Protezione UV fino al 97%</strong> certificata</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-sky-500 mt-2 flex-shrink-0" />
                      <span className="text-gray-600"><strong>Idrorepellenza</strong> e resistenza alle muffe</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-sky-500 mt-2 flex-shrink-0" />
                      <span className="text-gray-600"><strong>Oltre 1000 colori e fantasie</strong> disponibili in showroom</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Payment Options Section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="text-center mb-8">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Pagamenti Flessibili
              </span>
              <h2 className="text-2xl md:text-3xl font-light text-gray-900">
                Scegli come pagare la tua tenda da sole
              </h2>
            </div>
            <PaymentOptions variant="full" className="max-w-4xl mx-auto" />
          </FadeInOnScroll>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Vuoi una tenda da sole su misura?"
        description="Contattaci per una consulenza gratuita. Valuteremo insieme la soluzione perfetta per il tuo spazio."
        variant="light"
      />
    </Layout>
  );
};

export default TendeDaSolePage;
